import { Button, Checkbox, CheckboxGroup, ManagedCheckbox, ProgressBar, Spacer } from '@lokalise/louis';
import type { ChangeEvent, FC } from 'react';
import { useState } from 'react';

import LanguageIcon from '@app/components/LanguageIcon';
import showCorrectFlag from '@app/utils/languageFlag';

import {
    FadeoutSection,
    Item,
    Language,
    LanguageIconWrap,
    LanguageInfo,
    LanguageInfoEditable,
    LanguageName,
    List,
    Percentage,
    ProgressBarWrap,
    ProgressWrap,
    ToggleAllLabel,
    ToggleAllWrapper,
    ToggleWrap,
} from './styles';

const FadeoutLanguagesCount = 6;

export interface LanguageListItem {
    readonly id: string;
    readonly ccIso: string;
    readonly langIso: string;
    readonly name: string;
    readonly progress: number;
}

const prepareList = ({
    value,
    languages,
    isAllVisible,
}: {
    value: readonly string[];
    languages: readonly LanguageListItem[];
    isAllVisible: boolean;
}): readonly LanguageListItem[] => {
    if (!languages.length) {
        return [];
    }

    if (isAllVisible || !value.length) {
        return languages;
    }

    return languages.filter(({ id }) => value.includes(id));
};

const prepareAllLanguages = (
    baseLanguage: LanguageListItem | undefined,
    languages: readonly LanguageListItem[],
): readonly LanguageListItem[] => {
    const sortedLanguages = [...languages].sort((a, b) => a.name.localeCompare(b.name));

    if (!baseLanguage) {
        return sortedLanguages;
    }

    sortedLanguages.unshift(baseLanguage);

    return sortedLanguages;
};

type Value = {
    languages: readonly string[];
    customIso: {
        [languageId: string]: string;
    };
};

export type Props = {
    value: Value;
    languages: readonly LanguageListItem[];
    baseLanguage: LanguageListItem | undefined;
    onChange: (value: Value) => void;
};

const LanguageList: FC<Props> = ({ value, languages, baseLanguage, onChange }) => {
    const allLanguages = prepareAllLanguages(baseLanguage, languages);
    const [isAllChecked, setIsAllChecked] = useState(value.languages.length === allLanguages.length);
    const [isAllVisible, setIsAllVisible] = useState(false);

    const onAllToggle = () => {
        onChange({
            ...value,
            languages: isAllChecked ? [] : allLanguages.map(({ id }) => id),
        });
        setIsAllChecked((currentValue) => !currentValue);
    };
    const onSingleToggle = (e: ChangeEvent<HTMLInputElement>, newValue: readonly string[]) => {
        onChange({
            ...value,
            languages: newValue,
        });
        setIsAllChecked(newValue.length === allLanguages.length);
    };

    const list = prepareList({ value: value.languages, languages: allLanguages, isAllVisible });

    const isSomeChecked = !!value.languages.length;
    const isFadeoutSectionVisible = value.languages.length >= FadeoutLanguagesCount;

    return (
        <>
            <ToggleAllWrapper>
                <Checkbox value="toggle-all-languages" checked={isAllChecked} onChange={onAllToggle}>
                    <ToggleAllLabel>{`All (${value.languages.length}/${allLanguages.length})`}</ToggleAllLabel>
                </Checkbox>
            </ToggleAllWrapper>
            <List>
                <CheckboxGroup name="language-list" value={value.languages} onChange={onSingleToggle}>
                    {list.map(({ id, ccIso, langIso, name, progress }) => {
                        const iso = value.customIso?.[id] || langIso;

                        return (
                            <Item key={id}>
                                <Language>
                                    <span>
                                        <ManagedCheckbox value={id} />
                                    </span>
                                    <LanguageIconWrap>
                                        <LanguageIcon language={showCorrectFlag(name, ccIso)} width={15} />
                                    </LanguageIconWrap>
                                    <LanguageName>{name}</LanguageName>
                                    <LanguageInfo>
                                        <LanguageInfoEditable
                                            value={iso}
                                            placeholder="Custom ISO"
                                            label="Custom ISO"
                                            onChange={(val) =>
                                                onChange({
                                                    ...value,
                                                    customIso: {
                                                        ...value.customIso,
                                                        [id]: val,
                                                    },
                                                })
                                            }
                                        />
                                    </LanguageInfo>
                                </Language>
                                <ProgressWrap>
                                    <Percentage>{`${Math.floor(progress * 10) / 10}%`}</Percentage>
                                    <ProgressBarWrap>
                                        <ProgressBar progress={progress} height={6} />
                                    </ProgressBarWrap>
                                </ProgressWrap>
                            </Item>
                        );
                    })}
                </CheckboxGroup>
            </List>
            {isSomeChecked && !isAllChecked && (
                <ToggleWrap>
                    <Spacer top={isFadeoutSectionVisible && !isAllVisible ? 1 : 6}>
                        {isFadeoutSectionVisible && !isAllVisible && <FadeoutSection />}
                        <Button variant="secondary" onClick={() => setIsAllVisible((currentState) => !currentState)}>
                            {isAllVisible ? 'Hide unselected' : 'Show more languages'}
                        </Button>
                    </Spacer>
                </ToggleWrap>
            )}
        </>
    );
};

export default LanguageList;
